import { AssignmentInd, Close, MoreVert, People, Save } from '@mui/icons-material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Filter7Icon from '@mui/icons-material/Filter7';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { PersonReadDTO } from '../../api/dto';
import { TaskPreciseDTO } from '../../api/task';
import { FormGenerator } from '../../forms/FormGenerator';
import { personSchema } from '../../forms/schemas/person';
import { TFormElement } from '../../forms/types';
import { UiSchemaType } from '../../forms/UiSchemaType';
import { useAdminRole } from '../../hooks/useAdminRole';
import { useSelfAssignableRole } from '../../hooks/useSelfAssignableRole';
import { useApplicationContext } from '../application/ApplicationContextProvider';
import { useConfirmationDialog } from '../ConfirmationDialogContext';
import { TaskEscalationPersonList } from '../TaskEscalationPersonList';
import { commentSchema } from '../tasks/TaskActions';

interface TaskAssigneesDialogProps {
    open: boolean;
    onClose: () => void;
    mutateAddAssignees: ((data: { newAssignees: PersonReadDTO[]; comment: string | null }) => void) | null;
    mutateTimeout: (data: { date: Date | null; isEscalation: boolean }) => void;
    disabled: boolean;
    readOnly: boolean;
    task: TaskPreciseDTO;
}

const TaskAssigneesDialog: React.FC<TaskAssigneesDialogProps> = ({
    open,
    onClose,
    mutateAddAssignees,
    mutateTimeout,
    disabled,
    readOnly,
    task,
}) => {
    const intl = useIntl();
    const form = useForm<{ newAssignees: { person?: PersonReadDTO }[]; comment: string | null }>({
        mode: 'onSubmit',
        defaultValues: { newAssignees: [], comment: null },
    });
    const formRootElement: TFormElement = useMemo(
        () => ({
            type: UiSchemaType.FIELD_ARRAY,
            path: 'newAssignees',
            inline: true,
            element: personSchema({
                label: intl.formatMessage({ id: 'document.task.more.newAssignee' }),
                path: 'person',
            }),
            appendLabel: intl.formatMessage({ id: 'document.task.more.addNewAssignee' }),
            removeLabel: intl.formatMessage({ id: 'document.task.more.removeNewAssignee' }),
        }),
        [intl]
    );
    const submit = form.handleSubmit((data) => {
        if (!mutateAddAssignees) {
            throw new Error();
        }

        mutateAddAssignees({ newAssignees: data.newAssignees.map((a) => a.person!), comment: data.comment });
    });
    const watchNewAssignees = form.watch('newAssignees');
    const canSubmit = watchNewAssignees.length > 0 && watchNewAssignees.every((a) => !!a && !!a.person);

    const [selectedAssignees, setSelectedAssignees] = useState(new Set<number>());

    useEffect(() => {
        // Reset form when dialog is opened
        if (open) {
            form.reset();
            setSelectedAssignees(new Set());
        }
    }, [open]);

    return (
        <Dialog onClose={!disabled ? onClose : undefined} open={open} fullWidth maxWidth="sm">
            <DialogTitle>
                <FormattedMessage
                    id={!readOnly ? 'document.task.more.manageAssignees' : 'document.task.more.manageAssigneesReadOnly'}
                />
                <IconButton
                    onClick={onClose}
                    disabled={disabled}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2}>
                    <TaskEscalationPersonList
                        task={task}
                        selection={selectedAssignees}
                        updateSelection={setSelectedAssignees}
                        updateTimeout={mutateTimeout}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                    {selectedAssignees.size === 0 && !!mutateAddAssignees && !readOnly && (
                        <FormProvider {...form}>
                            <FormGenerator rootElement={formRootElement} form={form} disabled={disabled} />
                            {watchNewAssignees.length > 0 && (
                                <>
                                    <FormGenerator
                                        rootElement={commentSchema(intl, {
                                            toolTip: intl.formatMessage({
                                                id: 'document.task.assignees.commentTooltip',
                                            }),
                                        })}
                                        form={form}
                                        disabled={disabled}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<Save />}
                                        onClick={submit}
                                        disabled={disabled || !canSubmit}
                                    >
                                        <FormattedMessage id="document.action.save" />
                                    </Button>
                                </>
                            )}
                        </FormProvider>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

interface TaskManageButtonProps {
    task: TaskPreciseDTO;
    mutateAddAssignees: ((data: { newAssignees: PersonReadDTO[]; comment: string | null }) => void) | null;
    mutateTimeout: (data: { date: Date | null; isEscalation: boolean }) => void;
    disabled: boolean;
    moreActionsDisabled: boolean;
    readOnly: boolean;
}

export const TaskManageButton: React.FC<TaskManageButtonProps> = ({
    task,
    mutateAddAssignees,
    mutateTimeout,
    disabled,
    moreActionsDisabled,
    readOnly,
}) => {
    const intl = useIntl();
    const isAdmin = useAdminRole();
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
    const isMenuOpen = !!menuAnchorEl;

    const [isAssigneesDialogOpen, setAssigneesDialogOpen] = useState(false);
    const { currentUser } = useApplicationContext();
    const isSelfAssignable = useSelfAssignableRole();
    const isAlreadyAssignedToCurrentUser = task.assignees
        .flat(2)
        .some((assignee) => assignee.person.id === currentUser.id);

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const handleAssigneesDialogOpen = () => {
        setAssigneesDialogOpen(true);
        handleMenuClose();
    };
    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        // For normal users, open the dialog straight away, since it's the only option
        if (!isAdmin && !isSelfAssignable) {
            handleAssigneesDialogOpen();
        } else {
            setMenuAnchorEl(menuAnchorEl === null ? event.currentTarget : null);
        }
    };
    const handleTaskCamundaOpen = () => {
        if (task.workflowUrl) {
            window.open(task.workflowUrl);
        }
        handleMenuClose();
    };
    const handleTaskCibsevenOpen = () => {
        if (task.cibsevenWorkflowUrl) {
            window.open(task.cibsevenWorkflowUrl);
        }
        handleMenuClose();
    };

    const selfAssignConfirmationDialog = useConfirmationDialog();
    const handleSelfAssign = () => {
        selfAssignConfirmationDialog({
            prompt: intl.formatMessage({ id: 'document.task.selfAssign.confirm' }),
            onConfirm: () => {
                if (mutateAddAssignees) {
                    mutateAddAssignees({ newAssignees: [currentUser], comment: null });
                }
                handleMenuClose();
            },
        });
    };

    const hasCamundaLink = isAdmin && !!task.workflowUrl;
    const hasCibsevenLink = isAdmin && !!task.cibsevenWorkflowUrl;
    const hasWorkflowLinks = hasCamundaLink || hasCibsevenLink;
    return (
        <>
            <Tooltip
                title={intl.formatMessage({
                    id: !readOnly ? 'document.task.more.moreActions' : 'document.task.more.moreActionsReadOnly',
                })}
            >
                <IconButton component="span" size="small" disabled={moreActionsDisabled} onClick={handleMenuOpen}>
                    <MoreVert />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handleAssigneesDialogOpen}>
                    <ListItemIcon>
                        <People />
                    </ListItemIcon>
                    <FormattedMessage
                        id={
                            !readOnly
                                ? 'document.task.more.manageAssignees'
                                : 'document.task.more.manageAssigneesReadOnly'
                        }
                    />
                </MenuItem>
                {task.taskActive && isSelfAssignable && !isAlreadyAssignedToCurrentUser && (
                    <MenuItem onClick={handleSelfAssign}>
                        <ListItemIcon>
                            <AssignmentInd />
                        </ListItemIcon>
                        <FormattedMessage id="document.task.more.selfAssign" />
                    </MenuItem>
                )}
                {hasWorkflowLinks && <Divider />}
                {hasCamundaLink && (
                    <MenuItem onClick={handleTaskCamundaOpen}>
                        <ListItemIcon>
                            <AccountTreeIcon />
                        </ListItemIcon>
                        <FormattedMessage id="document.workflow.openCamunda" />
                    </MenuItem>
                )}
                {hasCibsevenLink && (
                    <MenuItem onClick={handleTaskCibsevenOpen}>
                        <ListItemIcon>
                            <Filter7Icon />
                        </ListItemIcon>
                        <FormattedMessage id="document.workflow.openCibseven" />
                    </MenuItem>
                )}
            </Menu>
            <TaskAssigneesDialog
                open={isAssigneesDialogOpen}
                onClose={() => setAssigneesDialogOpen(false)}
                disabled={disabled}
                readOnly={readOnly}
                mutateAddAssignees={mutateAddAssignees}
                mutateTimeout={mutateTimeout}
                task={task}
            />
        </>
    );
};
